<script>
import * as addressCat from '@/services/api/setting/general/address'
import * as industryCat from '@/services/api/setting/general/industry'
import * as department from '@/services/api/setting/employee/department'
import * as position from '@/services/api/setting/employee/position'
import * as unit from '@/services/api/setting/employee/unit'
import * as recruitment from '@/services/api/setting/employee/recruitment'
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    fields: { required: true },
    apiClient: { required: true },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
  },
  data() {
    return {
      page: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      queryName: {
        get: null,
        store: null,
        update: null,
        delete: null,
      },
      items: [],
      filteredItems: [],
      filterOn: [],
      rowUpdate: {},
      editedData: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedData: {},
      selectedDataLength: 0,
      selectedAll: false,
      formValues: {},
      companyId: this.getCompanyId(),
    }
  },
  computed: {
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
  },
  async mounted() {
    await this.$nextTick()
    await this.getData()
  },
  methods: {
    focusSearch() {
      this.$refs['searchInput'].focus()
    },
    transformCellValue(value) {
      if (value === false) return ''
      if (typeof value === 'number') return this.thousandFormat(value)
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    async getData() {
      if (!this.apiClient.get) {
        this.isBusy = false
        return
      }
      this.isBusy = true
      let res = {}
      switch (this.apiClient.get) {
        case 'GET_ADDRESS_CATEGORIES':
          res = await this.$apollo.query({
            query: addressCat.GET_ADDRESS_CATEGORIES,
            fetchPolicy: 'no-cache',
          })
          this.items = res.data.addressCategories.addressCategories
          break
        case 'GET_INDUSTRY_CATEGORIES':
          res = await this.$apollo.query({
            query: industryCat.GET_INDUSTRY_CATEGORIES,
            fetchPolicy: 'no-cache',
          })
          this.items = res.data.companyCategories.companyCategories
          break
        case 'GET_DEPARTMENT':
          res = await this.$apollo.query({
            query: department.GET_DEPARTMENT,
            fetchPolicy: 'no-cache',
            variables() {
              return {
                filter: {
                  level: 1,
                  isPosition: false,
                },
              }
            },
          })
          this.items = res.data.companyStructures.companyStructures
          break
        case 'GET_UNIT':
          res = await this.$apollo.query({
            query: department.GET_DEPARTMENT,
            fetchPolicy: 'no-cache',
            variables() {
              return {
                filter: {
                  level: 2,
                  isPosition: false,
                },
              }
            },
          })
          this.items = res.data.companyStructures.companyStructures
          break
        case 'GET_POSITION':
          res = await this.$apollo.query({
            query: department.GET_DEPARTMENT,
            fetchPolicy: 'no-cache',
          })
          this.items = res.data.companyStructures.companyStructures
          break
        case 'GET_RECRUITMENT':
          res = await this.$apollo.query({
            query: recruitment.GET_RECRUITMENT,
            fetchPolicy: 'no-cache',
          })
          this.items = res.data.employeeRecruitedAts.employeeRecruitedAts
          break
        default:
          this.items = []
          break
      }
      this.filteredItems = this.items
      this.totalCount = this.items.length
      if (this.items.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }
      this.isBusy = false
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.filteredItems = filteredItems
      this.totalCount = filteredItems.length
      this.page = 1
      const canDelete = this.filteredItems.filter(
        (item) => item.hasRelation === 0 && item.companyId === this.companyId
      )
      let flag = true
      const selectedKeys = Object.keys(this.selectedData).filter((key) => this.selectedData[key])
      for (const iterator of canDelete) {
        let index = selectedKeys.indexOf(iterator.text)
        if (index == -1) {
          flag = false
          break
        }
      }
      if (flag) this.selectedAll = true
      else this.selectedAll = false
    },
    async handleEditStart(data) {
      if (this.isEditingRow != null) return
      const refetchedData = data.item
      if (!refetchedData) return
      this.editedData = refetchedData

      this.items = this.items.map((item) => {
        if (item.text == data.item.text) return refetchedData
        return item
      })

      this.isEditingRow = data.index
      await this.$nextTick()
      this.$refs[`${this.fields[0].key}-ref`]?.$el.querySelector('input').focus()
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }
      this.fields[0].error = null
      this.isEditingRow = null
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/ {2}/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))
        return
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.editedData)
      }

      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(0, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    async submitStore(data) {
      let createdData = {}
      let success = true
      let errrorMsg = null
      switch (this.apiClient.store) {
        case 'CREATE_ADDRESS_CATEGORIES':
          createdData = await this.$apollo.mutate({
            mutation: addressCat.CREATE_ADDRESS_CATEGORIES,
            variables: {
              input: {
                text: data.text,
              },
            },
          })
          success = createdData.data.createAddressCategory.isSuccessful
          errrorMsg = createdData.data.createAddressCategory.message
          break
        case 'CREATE_INDUSTRY_CATEGORIES':
          createdData = await this.$apollo.mutate({
            mutation: industryCat.CREATE_INDUSTRY_CATEGORIES,
            variables: {
              input: {
                text: data.text,
              },
            },
          })
          success = createdData.data.createCompanyCategory.isSuccessful
          errrorMsg = createdData.data.createCompanyCategory.message
          break
        case 'CREATE_DEPARTMENT':
          createdData = await this.$apollo.mutate({
            mutation: department.DEPARTMENT_MUTATION_CREATE,
            variables: {
              input: {
                name: data.text,
                level: 1,
                isPosition: false,
              },
            },
          })
          success = createdData.data.createCompanyStructure.isSuccessful
          errrorMsg = createdData.data.createCompanyStructure.message
          break
        case 'CREATE_UNIT':
          createdData = await this.$apollo.mutate({
            mutation: unit.UNIT_MUTATION_CREATE,
            variables: {
              input: {
                name: data.text,
              },
            },
          })
          success = createdData.data.createUnit.isSuccessful
          errrorMsg = createdData.data.createUnit.message
          break
        case 'CREATE_POSITION':
          createdData = await this.$apollo.mutate({
            mutation: position.POSITION_MUTATION_CREATE,
            variables: {
              input: {
                name: data.text,
                departmentId: parseInt(data.department.structureId),
                unitName: data.unit.name,
              },
            },
          })
          success = createdData.data.createPosition.isSuccessful
          errrorMsg = createdData.data.createPosition.message
          break
        case 'CREATE_RECRUITMENT':
          createdData = await this.$apollo.mutate({
            mutation: recruitment.RECRUITMENT_MUTATION_CREATE,
            variables: {
              input: {
                text: data.text,
              },
            },
          })
          success = createdData.data.createEmployeeRecruitedAt.isSuccessful
          errrorMsg = createdData.data.createEmployeeRecruitedAt.message
          break
        default:
          createdData = {}
          break
      }
      if (!success) {
        this.fields[0].error = errrorMsg
        return false
      }
      if (!createdData) return false

      this.$mcToast.success(this.$t('global.alert.text.added'))
      this.fields[0].error = null
      return await this.getData()
    },
    async submitUpdate(data, oldData) {
      let updatedData = {}
      let success = true
      let errrorMsg = null
      switch (this.apiClient.update) {
        case 'UPDATE_ADDRESS_CATEGORIES':
          updatedData = await this.$apollo.mutate({
            mutation: addressCat.UPDATE_ADDRESS_CATEGORIES,
            variables: {
              input: {
                oldValue: oldData.text,
                newValue: data.text,
              },
            },
          })
          success = updatedData.data.updateAddressCategory.isSuccessful
          errrorMsg = updatedData.data.updateAddressCategory.message
          break
        case 'UPDATE_INDUSTRY_CATEGORIES':
          updatedData = await this.$apollo.mutate({
            mutation: industryCat.UPDATE_INDUSTRY_CATEGORIES,
            variables: {
              input: {
                oldValue: oldData.text,
                newValue: data.text,
              },
            },
          })
          success = updatedData.data.updateCompanyCategory.isSuccessful
          errrorMsg = updatedData.data.updateCompanyCategory.message
          break
        case 'UPDATE_DEPARTMENT':
          updatedData = await this.$apollo.mutate({
            mutation: department.DEPARTMENT_MUTATION_UPDATE,
            variables: {
              input: {
                nama: data.text,
                structureId: data.structureId,
              },
            },
          })
          success = updatedData.data.updateCompanyStructure.isSuccessful
          errrorMsg = updatedData.data.updateCompanyStructure.message
          break
        case 'UPDATE_UNIT':
          updatedData = await this.$apollo.mutate({
            mutation: unit.UNIT_MUTATION_UPDATE,
            variables: {
              input: {
                oldName: oldData.text,
                name: data.text,
              },
            },
          })
          success = updatedData.data.updateUnit.isSuccessful
          errrorMsg = updatedData.data.updateUnit.message
          break
        case 'UPDATE_POSITION':
          updatedData = await this.$apollo.mutate({
            mutation: position.POSITION_MUTATION_UPDATE,
            variables: {
              input: {
                nama: data.text,
                structureId: data.structureId,
                departmentId: data.department.structureId,
                unitName: data.unit.name,
              },
            },
          })
          success = updatedData.data.updatePosition.isSuccessful
          errrorMsg = updatedData.data.updatePosition.message
          break
        case 'UPDATE_RECRUITMENT':
          updatedData = await this.$apollo.mutate({
            mutation: recruitment.RECRUITMENT_MUTATION_UPDATE,
            variables: {
              input: {
                oldValue: oldData.text,
                newValue: data.text,
              },
            },
          })
          success = updatedData.data.updateEmployeeRecruitedAt.isSuccessful
          errrorMsg = updatedData.data.updateEmployeeRecruitedAt.message
          break
        default:
          updatedData = {}
          break
      }
      if (!success) {
        this.fields[0].error = errrorMsg
        return false
      }
      if (!updatedData) return false

      this.$mcToast.success(this.$t('global.alert.text.changed'))
      this.fields[0].error = null
      await this.getData()
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return this.$mcToast.error(this.$t('global.inputFormatError'))
    },
    handleSelect(checked, data) {
      this.selectedData = { ...this.selectedData, [data.item.text]: checked }
      const canDelete = this.filteredItems.filter(
        (item) => item.hasRelation === 0 && item.companyId === this.companyId
      )
      if (checked) {
        let flag = true
        this.selectedDataLength += 1
        const selectedKeys = Object.keys(this.selectedData).filter((key) => this.selectedData[key])
        for (const iterator of canDelete) {
          let index = selectedKeys.indexOf(iterator.text)
          if (index == -1) {
            flag = false
            break
          }
        }
        if (flag) this.selectedAll = true
        else this.selectedAll = false
      } else {
        this.selectedAll = false
        this.selectedDataLength -= 1
      }
    },
    handleSelectAll(event) {
      const canDelete = this.filteredItems.filter(
        (item) => item.hasRelation === 0 && item.companyId === this.companyId
      )
      if (event) {
        const selectAll = Object.fromEntries(canDelete.map((key) => [key.text, true]))
        this.selectedData = Object.assign({}, this.selectedData, selectAll)
        this.selectedDataLength = Object.values(this.selectedData).filter(
          (value) => value === true
        ).length
        this.selectedAll = true
      } else {
        const unselectAll = Object.fromEntries(canDelete.map((key) => [key.text, false]))
        this.selectedData = Object.assign({}, this.selectedData, unselectAll)
        this.selectedDataLength = Object.values(this.selectedData).filter(
          (value) => value === true
        ).length
      }
    },
    handleUnselectAll() {
      this.selectedData = {}
      this.selectedAll = false
      this.selectedDataLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.$emit('available-change', true)
      this.page = 1
      this.items = [{ id: 0 }, ...this.items]
    },
    handleDelete() {
      let promises = []
      const selectedKeys = Object.keys(this.selectedData).filter((key) => this.selectedData[key])
      switch (this.apiClient.delete) {
        case 'DELETE_ADDRESS_CATEGORIES':
          for (const iterator of selectedKeys) {
            promises.push(
              this.$apollo.mutate({
                mutation: addressCat.DELETE_ADDRESS_CATEGORIES,
                variables: {
                  input: {
                    text: iterator,
                  },
                },
              })
            )
          }
          break
        case 'DELETE_INDUSTRY_CATEGORIES':
          for (const iterator of selectedKeys) {
            promises.push(
              this.$apollo.mutate({
                mutation: industryCat.DELETE_INDUSTRY_CATEGORIES,
                variables: {
                  input: {
                    text: iterator,
                  },
                },
              })
            )
          }
          break
        case 'DELETE_DEPARTMENT':
        case 'DELETE_POSITION':
          for (const iterator of selectedKeys) {
            promises.push(
              this.$apollo.mutate({
                mutation: department.DEPARTMENT_MUTATION_DELETE,
                variables: {
                  input: {
                    structureId: iterator,
                  },
                },
              })
            )
          }
          break
        case 'DELETE_UNIT':
          for (const iterator of selectedKeys) {
            promises.push(
              this.$apollo.mutate({
                mutation: unit.UNIT_MUTATION_DELETE,
                variables: {
                  input: {
                    name: iterator,
                  },
                },
              })
            )
          }
          break
        case 'DELETE_RECRUITMENT':
          for (const iterator of selectedKeys) {
            promises.push(
              this.$apollo.mutate({
                mutation: recruitment.RECRUITMENT_MUTATION_DELETE,
                variables: {
                  input: {
                    text: iterator,
                  },
                },
              })
            )
          }
          break
        default:
          promises = []
          break
      }
      Promise.all(promises)
        .then(() => {
          this.getData()
          this.$mcToast.success(this.$t('global.alert.text.delete'))
          this.handleUnselectAll()
          this.$refs['mc-modal-delete'].hide()
        })
        .catch((e) => {
          console.error(e)
        })
    },
  },
}
</script>
<template>
  <div class="mc-editable-table">
    <div class="d-flex justify-content-between align-items-center">
      <div class="fw-bold text-nowrap card-title mb-0">{{ tableTitle }}</div>
      <div class="ml-auto d-flex justify-content-end mb-3">
        <McButton
          v-if="selectedDataLength"
          variant="secondary-red"
          class="mx-2"
          id="mc-delete-btn"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          variant="primary"
          :disabled="disabledGlobal"
          id="mc-add-btn"
          :text="$t('global.addField')"
          icon="icon-fill_plus"
          @click="handleAdd"
        />
      </div>
    </div>

    <div :class="{ 'card p-3 mt-2': withCard }">
      <b-row align-h="end">
        <b-col md="8" xl="10">
          <h6 class="mc-row-check mt-0" v-if="selectedDataLength">
            <span class="badge btn-success">{{ selectedDataLength }}</span>
            {{ $t('global.selectedRows') }}.
            <a @click="handleUnselectAll" class="color-primary">{{
              $t('global.action.clearAll')
            }}</a>
          </h6>
        </b-col>
        <b-col md="4" xl="2" v-if="searchable" :class="{ 'mt-0': withCard, 'mt-2': !withCard }">
          <b-input-group size="md" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              v-model="search"
              type="search"
              :placeholder="$t('global.search') + '...'"
              class="form-control ms-2"
              :disabled="disabledGlobal"
              ref="searchInput"
              @input="selectedAll = false"
            ></b-form-input>
          </b-input-group>
        </b-col>
      </b-row>
      <FormulateForm
        v-model="formValues"
        name="editable-table-form"
        ref="formRef"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive my-3 border border-1 mc-table-overflow">
          <b-table
            fixed
            show-empty
            class="editable-table"
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :sort-by="fields[0].key"
            :per-page="limit"
            :current-page="page"
            :filter="search"
            :filter-included-fields="filterOn"
            :empty-text="$t('global.emptyTable')"
            :empty-filtered-text="$t('global.emptyFilteredTable')"
            @filtered="onFiltered"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <div class="form-check" style="width: 10px">
                <b-form-checkbox
                  @change="handleSelectAll"
                  v-model="selectedAll"
                  :disabled="disabledGlobal"
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <div class="form-check">
                <b-form-checkbox
                  @change="(event) => handleSelect(event, data)"
                  :checked="selectedData[data.item.text]"
                  :disabled="
                    disabledGlobal ||
                    !hasAccessEditAndDelete(data.item.companyId) ||
                    data.item.hasRelation == 1
                  "
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-bind="{ ...field }"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :value="getInputInitialValue({ field, item })"
                  :disabled="isSubmitting"
                  validation-behavior="live"
                  error-behavior="live"
                  :ref="`${field.key}-ref`"
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <template v-else>
                {{ transformCellValue(value) }}
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button variant="outline" class="__accept-button btn" type="submit">
                  <i class="mc-icon icon-fill_checkmark"></i>
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel(data)"
                >
                  <i class="mc-icon icon-outline_close"></i>
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: disabled || isEditingRow !== null || isBusy,
                }"
                @click="handleEditStart(data)"
              >
                <i
                  v-if="hasAccessEditAndDelete(data.item.companyId) && data.item.hasRelation == 0"
                  class="mc-icon icon-outline_edit"
                ></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </FormulateForm>
      <div class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>{{ $t('global.show') }}</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              v-model="limit"
            >
            </b-form-select>
            <div>data.</div>
          </div>

          <div class="__info" v-if="totalCount === 0">
            <strong>{{ totalCount }}</strong> {{ $t('global.object.from') }}
            <strong>{{ totalCount }}</strong> data.
          </div>

          <div class="__info" v-else>
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
            {{ $t('global.object.from') }} <strong>{{ totalCount }}</strong> data.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            v-model="page"
          ></b-pagination>
        </div>
      </div>
    </div>
    <b-modal
      class="mc-modal"
      id="mc-modal-delete"
      ref="mc-modal-delete"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedDataLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
@import './style.scss';
</style>
